import { cva, type VariantProps } from 'class-variance-authority'
import { Slot } from 'radix-ui'
import type { ButtonHTMLAttributes } from 'react'
import { cn } from '../lib/cn'

/**
 * Abaixo de `desk` o alvo de toque é de 48px (07-responsividade). O botão pequeno e o de texto não
 * crescem: a área de toque vem de um pseudo-elemento centrado, e o desenho continua o mesmo.
 */
const touchArea =
  "max-desk:relative max-desk:after:absolute max-desk:after:inset-x-0 max-desk:after:top-1/2 max-desk:after:h-touch max-desk:after:-translate-y-1/2 max-desk:after:content-['']"

/**
 * Botão — componente 01.
 * Uma ação primária por tela; se houver duas, uma delas é `outline`.
 * Desabilitado sempre diz o motivo no próprio rótulo ("Selecione um documento"), nunca cinza mudo.
 * Carregando troca o rótulo por `loadingLabel` ("Enviando…") e bloqueia o clique — sem spinner solto.
 */
export const buttonVariants = cva(
  'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap select-none transition-[opacity,border-color] disabled:cursor-default aria-busy:cursor-default',
  {
    variants: {
      variant: {
        primary:
          'bg-ink font-display font-normal text-pampas hover:opacity-[.88] disabled:bg-alto disabled:text-label disabled:opacity-100 aria-busy:opacity-100',
        outline:
          'border border-hairline-heavy font-body font-light text-ink hover:border-ink disabled:border-hairline disabled:text-label',
        text: 'border-b border-hairline-heavy px-0 pb-[3px] font-body font-light text-ink hover:border-ink disabled:text-label',
        destructive:
          'bg-plum font-display font-normal text-pampas hover:opacity-[.88] disabled:bg-alto disabled:text-label disabled:opacity-100',
      },
      size: {
        md: 'min-h-touch-desk max-desk:min-h-touch px-[18px] py-[13px] text-[13px] leading-none',
        sm: `px-[14px] py-[9px] text-eyebrow ${touchArea}`,
        lg: 'min-h-touch w-full px-5 py-4 text-[14px] leading-none tracking-[.05em]',
      },
    },
    compoundVariants: [
      { variant: 'text', className: `min-h-0 px-0 py-0 max-desk:min-h-0 ${touchArea}` },
    ],
    defaultVariants: { variant: 'primary', size: 'md' },
  },
)

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
  /** Renderiza o filho como elemento raiz (ex.: `<Link>`), mantendo o estilo. */
  asChild?: boolean
  /** Enquanto verdadeiro, o rótulo é substituído por `loadingLabel` e o clique é bloqueado. */
  loading?: boolean
  loadingLabel?: string
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  loading = false,
  loadingLabel = 'Enviando…',
  disabled,
  children,
  type,
  ...props
}: ButtonProps) {
  const Comp = asChild ? Slot.Root : 'button'
  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      type={asChild ? undefined : (type ?? 'button')}
      {...props}
    >
      {loading ? loadingLabel : children}
    </Comp>
  )
}
